import {
  BaseEquipment,
  BookingState,
  EnabledFeatures,
  Equipment,
  Gym,
  GymTypes,
  IEnabledFeatures,
  ProgramType,
  TimeSpan,
} from 'fitbeat.models';
import moment from 'moment';
import {
  getActivityTypeForAreaCategory,
} from '../../config/workout-activity-mappings/fitillion-workout-activity-type-mappings';
import { ProgramsConfig } from '../../config/workoutgoalconfig';
import { AreaCategory, staticFunctionalAreaZones, staticMachineAreaZones } from '../../config/zone-mappings';
import {
  CardioOrResistance, Exercise, IUserRecommendedSettings, Level, WorkoutSettings,
} from '../../entities';
import { UserWorkoutSession } from '../../entities/workoutsession/userworkoutsession';
import {
  DbGymRepository, IUserProfileRepository, IWorkoutRepository, WorkoutFilter,
} from '../../persistence';

export class WorkoutProgramRecommendations {
  public static doesWorkoutsHaveRecommendedSettings(userWorkoutSessions: UserWorkoutSession[]): boolean {
    const workoutsWithRecommendations = userWorkoutSessions.filter(
      (userWorkoutSession: UserWorkoutSession) => !!userWorkoutSession.recommendedSettings,
    );

    return workoutsWithRecommendations.length !== 0;
  }

  public static areSettingsTheSameForRecommendation(a: WorkoutSettings, b: WorkoutSettings) {
    return a.cardioOrResistance === b.cardioOrResistance
      && Exercise.compare3BodyGroup(a.threeBodyGroups, b.threeBodyGroups);
  }

  public static forceSettingsBasedOnWorkoutArea(settings: WorkoutSettings, areaCategory: AreaCategory) {
    const isStaticCardioMachine = staticMachineAreaZones.includes(areaCategory);
    if (isStaticCardioMachine) {
      settings.forceSettingsDueToAreaCategory = true;
      settings.cardioOrResistance = CardioOrResistance.Cardio;
    }

    const isStaticFunctionalArea = staticFunctionalAreaZones.includes(areaCategory);
    if (isStaticFunctionalArea) {
      // don't set forceSettingsDueToAreaCategory when only changing equipment
      settings.equipment = Exercise.getEquipmentForStudioArea(areaCategory);
      if (areaCategory === 'boxing') {
        settings.allowBagContact = true;
        settings.cardioOrResistance = CardioOrResistance.Cardio;
      }
    }

    const forcedBodyGroups = Exercise.forcedBodyGroupsForStudioArea(areaCategory);
    if (forcedBodyGroups) {
      settings.forceSettingsDueToAreaCategory = true;
      settings.threeBodyGroups = forcedBodyGroups;
    }

    settings.activityType = getActivityTypeForAreaCategory(settings.cardioOrResistance, areaCategory);
  }

  constructor(
    private workoutRepo: IWorkoutRepository,
    private userRepo: IUserProfileRepository,
    private logger: any,
  ) {
  }

  public async GetUserRecommendedSettings(
    userId: string,
    workoutDate: Date,
    enabledFeatures: IEnabledFeatures,
    gym: Gym,
    userProgram: ProgramType = ProgramType.NotSet,
    currentDateTime: Date = new Date(),
  ): Promise<IUserRecommendedSettings> {
    const settings = await this.GetRecommendedSettingsForDate(
      userId,
      workoutDate,
      enabledFeatures,
      gym,
      userProgram,
      currentDateTime,
    );

    if (!settings) {
      throw new Error('Failed to determine recommended workout settings for user');
    }

    const calisthenic = new WorkoutSettings(settings);
    WorkoutProgramRecommendations.forceSettingsBasedOnWorkoutArea(calisthenic, 'calisthenic');

    return {
      settings,
      calisthenic,
    };
  }

  public async GetRecommendedSettingsForDate(
    userId: string,
    workoutDate: Date,
    enabledFeatures: IEnabledFeatures,
    gym: Gym,
    userProgram: ProgramType = ProgramType.NotSet,
    currentDateTime: Date = new Date(),
  ): Promise<WorkoutSettings | undefined> {
    const workoutTimeToUse = this.getWorkoutTimeToUse(
      workoutDate,
      gym,
    );
    const [userWorkoutSettings, workoutsFrom28DaysAgo] = await Promise.all([
      this.getUsersWorkoutSettings(userId, enabledFeatures),
      this.getSortedWorkoutsFourWeeksAgo(userId, currentDateTime),
    ]);
    const filteredWorkouts = workoutsFrom28DaysAgo.filter(
      (a: UserWorkoutSession) => a.startTime.getTime() < workoutTimeToUse.getTime(),
    );
    const programSettings = this.getProgramSettings(userProgram, userWorkoutSettings, gym);
    if (programSettings == null || programSettings.length === 0) {
      return undefined;
    }

    const workout = new UserWorkoutSession(userId, 20);
    workout.startTime = workoutDate;
    workout.settings = new WorkoutSettings();
    const settings = this.GetRecommendedSettingsForWorkout(
      workout,
      filteredWorkouts,
      programSettings,
      userWorkoutSettings,
      gym,
    );
    return settings;
  }

  public async GetWorkoutsForDashboardWithRecommendations(
    userId: string,
    enabledFeatures: IEnabledFeatures,
    userProgram: ProgramType = ProgramType.NotSet,
    currentDateTime: Date = new Date(),
    userWorkoutSettings?: WorkoutSettings,
  ): Promise<UserWorkoutSession[]> {
    if (userProgram === ProgramType.NotSet) {
      this.logger.warn(`No workout program for user: ${userId} userProgram: ${userProgram}`);
      const workouts = await this.workoutRepo.getConfirmedBookingsAndInvitesAndProvisional(userId);
      return workouts.filter(
        WorkoutFilter.getExcludeWorkoutsDuringWarmdownFilter(currentDateTime),
      );
    }

    const workoutsFrom28DaysAgo = await this.getSortedWorkoutsFourWeeksAgo(userId, currentDateTime);
    if (!userWorkoutSettings) {
      userWorkoutSettings = await this.getUsersWorkoutSettings(userId, enabledFeatures);
    }

    this.SetRecommendedSettingsForFutureWorkouts(
      workoutsFrom28DaysAgo,
      currentDateTime,
      userWorkoutSettings,
      userProgram,
    );
    const upcomingWorkouts = workoutsFrom28DaysAgo
      .filter((workout) => workout.endTime >= currentDateTime)
      .filter(WorkoutFilter.getExcludeWorkoutsDuringWarmdownFilter(currentDateTime))
      .map((workout) => {
        // this must Object.assign in order to copy functions over (at least for type matching)
        /* eslint-disable prefer-object-spread */
        const result = Object.assign({}, workout);
        const isStudioBooking = !UserWorkoutSession.isHomeBooking(result, DbGymRepository.getByName);
        const keepExerciseSessionsToCalculateCurrentZoneInApp = result.checkedIn && isStudioBooking;
        if (!keepExerciseSessionsToCalculateCurrentZoneInApp) {
          result.exerciseSessions = [];
        }
        return result;
      });
    await this.resolvePairedWorkouts(upcomingWorkouts);
    return upcomingWorkouts;
  }

  private async resolvePairedWorkouts(sessions: UserWorkoutSession[]) {
    for (const session of sessions) {
      if (!session.pairedSessionReference) {
        continue;
      }
      const pairedSession = await this.workoutRepo.getWorkoutSessionFromId(session.pairedSessionReference);
      session.pairedSession = pairedSession;
    }
  }

  private async getUsersWorkoutSettings(userId: string, enabledFeatures: IEnabledFeatures): Promise<WorkoutSettings> {
    const user = await this.userRepo.get(userId);
    const defaultLevel = Level.Basic;
    const defaultEquipment: Equipment[] = [Equipment.Bodyweight];
    if (user) {
      const equipment = EnabledFeatures.calculateEquipmentSettings(enabledFeatures, user.defaultEquipment);

      return new WorkoutSettings({
        level: user.skillLevel || defaultLevel,
        equipment,
      });
    }
    return new WorkoutSettings({
      level: defaultLevel,
      equipment: defaultEquipment,
    });
  }

  private async getSortedWorkoutsFourWeeksAgo(userId: string, currentDateTime: Date): Promise<UserWorkoutSession[]> {
    const fourWeeksInDays = 28;
    const fourWeeksAgo = TimeSpan.getXDaysAgo(fourWeeksInDays, currentDateTime);
    const workouts = await this.workoutRepo.getWorkoutsAfterPointInTime(userId, fourWeeksAgo);
    // ensure sorted by ascending date
    return workouts
      .sort((a: UserWorkoutSession, b: UserWorkoutSession) => a.startTime.getTime() - b.startTime.getTime());
  }

  // start from furthest in the future to past
  private SetRecommendedSettingsForFutureWorkouts(
    workouts: UserWorkoutSession[],
    currentDateTime: Date,
    userWorkoutSettings: WorkoutSettings,
    userProgram: any,
  ) {
    const workoutsToSave: UserWorkoutSession[] = [];

    for (const workout of workouts) {
      if (workout.startTime > currentDateTime && workout.bookingState === BookingState.ConfirmedBooking) {
        const workoutsBeforeWorkoutToRecommend = workouts.slice(0, workouts.indexOf(workout));

        const workoutGym = DbGymRepository.getByName(workout.gymName);
        const programSettings = this.getProgramSettings(userProgram, userWorkoutSettings, workoutGym);
        if (programSettings == null || programSettings.length === 0) {
          return;
        }

        const shouldForceFullBody = (
          this.hasNoEquipmentAtHome(workoutGym, workout.settings) || workoutGym.type === GymTypes.Outdoor);

        const goalSettings = this.GetRecommendedSettingsForWorkout(
          workout,
          workoutsBeforeWorkoutToRecommend,
          programSettings,
          userWorkoutSettings,
          workoutGym,
        );
        if (goalSettings || shouldForceFullBody) {
          if (goalSettings && !WorkoutProgramRecommendations
            .areSettingsTheSameForRecommendation(workout.settings, goalSettings)
          ) {
            workout.settings = goalSettings;
          }
          if (shouldForceFullBody) {
            workout.settings.threeBodyGroups = WorkoutSettings.getFullBody();
          }
          workoutsToSave.push(workout);
        }
      }
    }

    for (const workout of workoutsToSave) {
      // TODO: should this be updating in the background
      this.workoutRepo.updateWorkoutSettings(workout.uniqueReference, workout.settings)
        .catch((e) => this.logger.warn(`failed to updateWorkoutSettings for ${workout.uniqueReference}`, e));
    }
  }

  private GetRecommendedSettingsForWorkout(
    workoutToRecommend: UserWorkoutSession,
    workoutsBeforeWorkoutToRecommend: UserWorkoutSession[],
    programSettings: WorkoutSettings[],
    userWorkoutSettings: WorkoutSettings,
    gym: Gym,
  ): WorkoutSettings | undefined {
    const previousWorkoutsInProgram = this
      .getpreviousWorkoutsInProgram(workoutsBeforeWorkoutToRecommend, programSettings);

    if (programSettings.length === 0
      || workoutToRecommend.settings.ignoreProgramRecommendations === true
      || workoutToRecommend.settings.forceSettingsDueToAreaCategory === true) {
      return undefined; // nothing to recommend
    }

    const nextSettings = this.calculateNextSettings(previousWorkoutsInProgram, programSettings);

    if (WorkoutProgramRecommendations.areSettingsTheSameForRecommendation(workoutToRecommend.settings, nextSettings)) {
      return undefined; // settings already correct, no need for recommendation
    }

    return this.assignRecommendedSettings(workoutToRecommend, nextSettings, gym, userWorkoutSettings);
  }

  // note that what complicated this method is that programs can have multiples of the same workouts
  // so to determine whats next we have to look at the last workout and the second last one to determine
  // what is next
  private calculateNextSettings(
    previousWorkoutsInProgram: UserWorkoutSession[],
    programSettings: WorkoutSettings[],
  ): WorkoutSettings {
    const defaultSettings = programSettings[0]; // default to first
    if (previousWorkoutsInProgram == null || previousWorkoutsInProgram.length === 0) {
      return defaultSettings;
    }
    const lastWorkoutInProgram = previousWorkoutsInProgram.pop();
    if (lastWorkoutInProgram == null) {
      return defaultSettings;
    }

    const lastWorkoutInProgramSettings = this.getWorkoutSettingsForCalculation(lastWorkoutInProgram);
    const instancesOfLastWorkoutSetting = programSettings.filter(
      (s) => WorkoutProgramRecommendations.areSettingsTheSameForRecommendation(s, lastWorkoutInProgramSettings),
    );

    if (instancesOfLastWorkoutSetting.length === 0) {
      return defaultSettings;
    }

    const secondLastWorkoutInProgram = previousWorkoutsInProgram.pop();
    let indexOfSecondLastWorkoutSetting = -1;
    if (secondLastWorkoutInProgram != null) {
      indexOfSecondLastWorkoutSetting = programSettings.findIndex(
        (s) => WorkoutProgramRecommendations
          .areSettingsTheSameForRecommendation(s, this.getWorkoutSettingsForCalculation(secondLastWorkoutInProgram)),
      );
    }

    // defaults to first
    let indexOfLastUsedSetting = programSettings.indexOf(instancesOfLastWorkoutSetting[0]);

    if (indexOfSecondLastWorkoutSetting !== -1) { // of there is only one
      for (const instanceOfLastWorkoutSetting of instancesOfLastWorkoutSetting) {
        const currentIndex = programSettings.indexOf(instanceOfLastWorkoutSetting);
        // check if the second last index workout is followed by the last instance workout
        // to pinpoint the position of the correct last workout
        if (this.getNextRotatingIndex(indexOfSecondLastWorkoutSetting, programSettings) === currentIndex) {
          indexOfLastUsedSetting = currentIndex;
        }
      }
    }

    const indexOfNext = this.getNextRotatingIndex(indexOfLastUsedSetting, programSettings);
    const nextSettings = programSettings[indexOfNext];
    return nextSettings;
  }

  private getNextRotatingIndex(indexOfLastUsedSetting: number, programSettings: WorkoutSettings[]) {
    return (indexOfLastUsedSetting + 1) % programSettings.length;
  }

  private getpreviousWorkoutsInProgram(
    workoutsBeforeWorkoutToRecommend: UserWorkoutSession[],
    programSettings: WorkoutSettings[],
  ) {
    return workoutsBeforeWorkoutToRecommend.filter((w) => {
      const workoutSettings = this.getWorkoutSettingsForCalculation(w);
      for (const programSetting of programSettings) {
        if (WorkoutProgramRecommendations.areSettingsTheSameForRecommendation(programSetting, workoutSettings)
          && workoutSettings.ignoreProgramRecommendations !== true
          && workoutSettings.forceSettingsDueToAreaCategory !== true
          && !w.importOrigin
        ) {
          return true;
        }
      }
      return false;
    });
  }

  private getWorkoutSettingsForCalculation(w: UserWorkoutSession) {
    // recommended settings take priority
    return w.recommendedSettings
      ? w.recommendedSettings
      : w.settings;
  }

  private getProgramSettings(userProgram: ProgramType, userWorkoutSettings: WorkoutSettings, gym: Gym)
    : WorkoutSettings[] | undefined {
    const homeGymWithNoEquipment = this.hasNoEquipmentAtHome(gym, userWorkoutSettings);
    const isOutdoorGym = gym.type === GymTypes.Outdoor;
    const getFullBodyProgram = homeGymWithNoEquipment || isOutdoorGym;
    return ProgramsConfig.getProgramConfig(userProgram, getFullBodyProgram);
  }

  private assignRecommendedSettings(
    workoutToRecommend: UserWorkoutSession,
    nextSettings: WorkoutSettings,
    gym: Gym,
    userWorkoutSettings?: WorkoutSettings,
  ): WorkoutSettings {
    const recommendedSettings = new WorkoutSettings(workoutToRecommend.settings);
    WorkoutSettings.applyProgramRecommendations(recommendedSettings, nextSettings);
    if (userWorkoutSettings) {
      recommendedSettings.level = userWorkoutSettings.level;
      if (gym.type === GymTypes.Home) {
        if (recommendedSettings.cardioOrResistance === CardioOrResistance.Balance) {
          const containsDumbbells = BaseEquipment.containsDumbbells(userWorkoutSettings.equipment);
          recommendedSettings.equipment = containsDumbbells
            ? [...userWorkoutSettings.equipment]
            : [Equipment.Bodyweight];
        } else {
          recommendedSettings.equipment = userWorkoutSettings.equipment;
        }
      } else {
        recommendedSettings.equipment = [];
      }
    }
    return recommendedSettings;
  }

  private hasNoEquipmentAtHome(gym: Gym, settings: WorkoutSettings): boolean {
    return gym.type === GymTypes.Home && !WorkoutSettings.hasEquipment(settings);
  }

  private getWorkoutTimeToUse(workoutDate: Date, gym: Gym): Date {
    const now = moment(workoutDate).tz(gym.timezone).startOf('day');
    return now.toDate();
  }
}
